import { Grid, Card, CardContent, CardHeader, Typography, Divider, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import { Customer } from '../../../../models/customer';
import { SkeletonIndicator } from '../../../../components/skeleton-indicator.component';
import { formatValue } from '../../../../utils/data-format';
import { PHONENUMBER_FORMAT, ZIPCODE_FORMAT } from '../../../../constants';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

interface CustomerInfoProps {
  customer?: Customer;
  isLoading?: boolean;
}

interface ComponentCell {
  isLoading: any;
  heading: string;
  value: any;
}

const styling = {
  root: { borderRadius: '10px' },
  textColor: { color: '$primary-color' },
  headingStyle: { color: '#0000b5', paddingBottom: '10px' },
  labelStyle: { color: '#0000b5', fontSize: '14px' },
  valueStyle: { color: '#000', fontWeight: '500', fontSize: '13px' },
};

const ComponentCell = ({ isLoading, heading, value }: ComponentCell) => {
  return (
    <Grid container spacing={0.5}>
      <Grid item lg={12} sm={12} xl={12} xs={12}>
        <Typography variant="body1" color="textPrimary" style={styling.labelStyle}>
          {heading}
        </Typography>
      </Grid>
      <Grid item lg={12} sm={12} xl={12} xs={12}>
        {!isLoading ? (
          <Typography variant="body1" color="text.secondary" style={styling.valueStyle} className="valueStyles">
            {value ? value : '-----------'}
          </Typography>
        ) : (
          <SkeletonIndicator isLoading={isLoading} count={1} variant="h6" />
        )}
      </Grid>
    </Grid>
  );
};

export const CustomerInfo = ({ customer, isLoading }: CustomerInfoProps) => {
  return (
    <Card elevation={0} sx={{ mr: 2 }}>
      <CardHeader
        className="subHead"
        title={
          <Typography variant="h3" color="textPrimary" style={styling.headingStyle}>
            CUSTOMER INFO
          </Typography>
        }
      />
      <Divider sx={{ borderBottomWidth: '2px' }} />
      <CardContent>
        <Grid container spacing={2} className="custBoxValues">
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <ComponentCell isLoading={isLoading} heading="Customer Type" value={customer?.customerTypeNames} />
          </Grid>
          <Grid item lg={12}>
            <Divider />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Grid container spacing={1}>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell isLoading={isLoading} heading="Contact Email" value={customer?.contactEmail} />
              </Grid>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell
                  isLoading={isLoading}
                  heading="Contact Phone"
                  value={formatValue(customer?.contactPhone?.toString() ?? '', PHONENUMBER_FORMAT, '#').formattedValue}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item lg={12}>
            <Divider />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Grid container spacing={1}>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell isLoading={isLoading} heading="Street Address" value={customer?.street} />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <ComponentCell isLoading={isLoading} heading="City" value={customer?.city} />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <ComponentCell isLoading={isLoading} heading="State" value={customer?.stateName} />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <ComponentCell
                  isLoading={isLoading}
                  heading="Zip Code"
                  value={formatValue(customer?.zipcode?.toString() ?? '', ZIPCODE_FORMAT, '#').formattedValue}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Grid container spacing={1}>
              {/* <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell isLoading={isLoading} heading="Invoice Due Days" value={customer?.invoiceTermText} />
              </Grid> */}
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell
                  isLoading={isLoading}
                  heading="Purchase Order Requirements"
                  value={customer?.customerPoRequirement}
                />
              </Grid>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell isLoading={isLoading} heading="Account Managers" value={customer?.accountManagerNames} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Grid container spacing={1}>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell isLoading={isLoading} heading="Customer Code" value={customer?.customerCode} />
              </Grid>
            </Grid>
          </Grid>
          {
            customer?.syncWithDux && (
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <Grid item lg={6} sm={6} xl={6} xs={6}>
                  <ComponentCell isLoading={isLoading} heading="DUX Synced" value={customer?.isDuxSyncSuccess ? 'Success' : 'Fail'} />
                </Grid>
              </Grid>
            )
          }
        </Grid>
      </CardContent>
    </Card>
  );
};
